import React, { ReactElement } from 'react';
import { countryValueResolver } from 'helpers/location';
import env from 'config/environment';
import { CommonOnUpdate } from 'types/props';
import { FieldModel, LocationModel } from 'forms/shared/types';
import { COUNTRY_FOREIGN, COUNTRY_US } from 'constants/enums/locations';
import { i18n } from 'config/locales';
import Branch from '../Branch';
import ValidationElement from '../ValidationElement';
import Street from '../Street';
import State from '../State';
import City from '../City';
import Country from '../Country';
import County from '../County';
import ZipCode from '../ZipCode';
import Layouts from './Layouts';
import { setAttribute } from '../shared/setAttribute';
import LocationValidator, { countryString } from '../../../validators/location';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';
import Text from '../Text';
import MilitaryInstallation from '../MilitaryInstallation';

/**
 * @deprecated
 */
const mappingWarning = (property) => {
  if (!env.IsTest()) {
    console.warn(`Could not map location property '${property}' in ToggleableLocation `);
  }
};

const branchValue = (inUSA) => {
  if (inUSA === true) { return 'Yes'; }
  if (inUSA === false) { return 'No'; }

  return '';
};

interface Props extends Partial<LocationModel>, ValidationElementProps {
  domesticTitle?: string;
  foreignTitle?: string;
  militaryDiplomaticInstallationNameLabel?: string
  onUpdate?: CommonOnUpdate
  domestic?: any
  // TODO: Update type to be specific to what it can accept
  domesticFields: string[]
  // TODO: Update type to be specific to what it can accept
  internationalFields: string[]
  attribute?: string
  streetLabel?: string
  internationalStreetLabel?: string
  street2Label?: string
  cityLabel?: string
  stateLabel?: string
  zipcodeLabel?: string
  countryLabel?: string
  countyLabel?: string
  countyPlaceholder?: string
  postOfficeStreetPlaceholder?: string
  postOfficeStateLabel?: string
  postOfficeZipcodeLabel?: string
  streetPlaceholder?: string
  cityPlaceholder?: string
  statePlaceholder?: string
  zipcodePlaceholder?: string
  countryPlaceholder?: string
  label?: string
  name?: string
  required?: boolean
}

interface TLState {
  uid: string
  suggestions: any[]
}

export default class ToggleableLocation extends ValidationElement<Props, TLState> {
  constructor(props) {
    super(props);

    this.state = {
      suggestions: [],
      uid: `${this.props.name}-${super.guid()}`,
    };
    this.errors = [];
  }

  update = (updateValues) => {
    if (this.props.onUpdate) {
      const nextValues = {
        apoFpoDpoPostcode: this.props.apoFpoDpoPostcode,
        isMilitaryDiplomaticAddress: this.props.isMilitaryDiplomaticAddress,
        militaryDiplomaticInstallationName: this.props.militaryDiplomaticInstallationName,
        militaryDiplomaticInstallationNameExplanation: this.props.militaryDiplomaticInstallationNameExplanation,
        militaryInstallationNameNotApplicable: this.props.militaryInstallationNameNotApplicable,
        street: this.props.street,
        city: this.props.city,
        zipcode: this.props.zipcode,
        state: this.props.state,
        country: this.props.country,
        countryComments: this.props.countryComments,
        county: this.props.county,
        domestic: this.props.domestic,
        domesticFields: this.props.domesticFields,
        internationalFields: this.props.internationalFields,
        inUSA: this.props.inUSA,
        ...updateValues,
      };
      this.props.onUpdate(nextValues);
    }
  };

  onAddressUpdate = (nextValue) => {
    const { name, value } = nextValue;

    this.update({
      [name]: value,
    });
  };

  updateApoFpoDpoPostcode = (apoFpoDpoPostcodeFieldModel: FieldModel) => {
    this.update({
      apoFpoDpoPostcode: apoFpoDpoPostcodeFieldModel.value,
    });
  };

  updateCountry = (values) => {
    this.update({
      country: { value: values.value ?? '' },
      countryComments: values.comments,
    });
  };

  updateMilitaryDiplomaticInstallationName = (militaryDiplomaticInstallationNameFieldModel: FieldModel) => {
    this.update({
      militaryDiplomaticInstallationName: militaryDiplomaticInstallationNameFieldModel.value,
    });
  };

  updateMilitaryInstallation = (values) => {
    this.update({
      apoFpoDpoPostcode: this.props.apoFpoDpoPostcode,
      isMilitaryDiplomaticAddress: this.props.isMilitaryDiplomaticAddress,
      militaryDiplomaticInstallationName: this.props.militaryDiplomaticInstallationName,
      militaryInstallationNameNotApplicable: this.props.militaryInstallationNameNotApplicable,
      militaryDiplomaticInstallationNameExplanation: this.props.militaryDiplomaticInstallationNameExplanation,
      ...values,
    });
  };

  updateToggle = (option) => {
    // Set existing errors to null when toggling fields
    this.props.onError(
      option.value,
      this.errors.map((err) => ({
        code: err.code,
        valid: null,
        uid: err.uid,
      }))
    );

    switch (option.value) {
      case 'Yes':
        this.update({ inUSA: true, country: { value: COUNTRY_US } });
        break;
      case 'No':
        this.update({ inUSA: false, country: { value: COUNTRY_FOREIGN } });
        break;
      default:
    }
  };

  zipcodeInstate = () => {
    const validator = new LocationValidator(this.props);

    return validator.validZipcodeState();
  };

  onError = (value, arr) => {
    // eslint-disable-next-line no-param-reassign
    arr = arr.map((err) => ({
      code: `toggleablelocation.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    const requiredErr = arr.concat(
      this.constructor.errors.map((err) => ({
        code: `toggleablelocation.${err.code}`,
        valid: err.func(value, { ...this.props }),
        uid: this.state.uid,
      }))
    );

    this.storeErrors(requiredErr);
    this.props.onError(value, requiredErr);
    return arr;
  };

  storeErrors = (errors) => {
    const newErrors = [...errors];

    for (let i = 0; i < newErrors.length; i += 1) {
      const e = newErrors[i];
      const idx = this.errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        this.errors[idx] = { ...e };
      } else {
        this.errors.push({ ...e });
      }
    }
  };

  render() {
    const { inUSA } = this.props;
    const instateZipcode = this.zipcodeInstate();

    const requiredLayouts = this.props.layout === Layouts.IDENTIFICATION_BIRTH_PLACE
      || this.props.layout === Layouts.OFFENSE
      || this.props.layout === Layouts.BIRTHPLACE;

    const domesticFields = this.props.domesticFields.map((field) => {
      const key = `domestic-${field}`;
      switch (field) {
        case 'apoFpoDpoPostcode':
          return this.renderApoFpoDpoPostcode(key);
        case 'militaryDiplomaticInstallationName':
          return this.renderMilitaryDiplomaticInstallationName(key);
        case 'street':
          return (
            <Street
              attribute={`${this.props.attribute}.street`}
              name="street"
              label={this.props.streetLabel}
              className="mailing street"
              key={key}
              placeholder={this.props.streetPlaceholder}
              value={this.props.street}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
          );
        case 'city':
          return (
            <City
              attribute={`${this.props.attribute}.city`}
              name="city"
              className="city"
              key={key}
              label={this.props.cityLabel}
              placeholder={this.props.cityPlaceholder}
              value={this.props.city}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={
                requiredLayouts ? this.props.required && !this.props.county : this.props.required
              }
              requireCity={
                requiredLayouts ? this.props.required && !this.props.county : this.props.required
              }
            />
          );
        case 'county':
          return (
            <County
              attribute={`${this.props.attribute}.county`}
              name="county"
              key={key}
              label={this.props.countyLabel}
              value={this.props.county}
              className="county"
              placeholder={this.props.countyPlaceholder}
              maxlength="255"
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={
                requiredLayouts ? this.props.required && !this.props.city : this.props.required
              }
              requireCounty={
                requiredLayouts ? this.props.required && !this.props.city : this.props.required
              }
            />
          );
        case 'state':
          return (
            <State
              attribute={`${this.props.attribute}.state`}
              name="state"
              key={key}
              className="state"
              label={this.props.stateLabel}
              placeholder={this.props.statePlaceholder}
              value={this.props.state}
              includeStates="true"
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              onBlur={this.props.onBlur}
              required={this.props.required}
            />
          );
        case 'stateZipcode':
          return (
            <div className="state-zip-wrap" key={key}>
              <State
                attribute={`${this.props.attribute}.state`}
                name="state"
                className="state"
                label={this.props.stateLabel}
                placeholder={this.props.statePlaceholder}
                value={this.props.state}
                includeStates="true"
                onUpdate={this.onAddressUpdate}
                onError={this.onError}
                onFocus={this.props.onFocus}
                onBlur={this.props.onBlur}
                required={this.props.required}
              />
              <ZipCode
                attribute={`${this.props.attribute}.zipcode`}
                name="zipcode"
                key="us_zipcode"
                className="zipcode"
                label={this.props.zipcodeLabel}
                placeholder={this.props.zipcodePlaceholder}
                value={this.props.zipcode}
                status={instateZipcode}
                onUpdate={this.onAddressUpdate}
                onError={this.onError}
                onFocus={this.props.onFocus}
                required={this.props.required}
              />
            </div>
          );
        case 'usMilitaryInstallation':
          const apoFpoDpoPostcodeLabel = '';

          // PVQ: Is this a U.S. military installation?
          const branchLabel: string = i18n.t('address.pvq.installation.branch.label');

          // PVQ: What is the name of this U.S. military installation?
          const nameTitle: string = i18n.t('address.pvq.installation.name.title');

          const showApoFpoDpoPostcode = false;

          return this.renderMilitaryInstallation(
            apoFpoDpoPostcodeLabel,
            branchLabel,
            key,
            nameTitle,
            showApoFpoDpoPostcode
          );
        default:
          return null;
      }
    });

    const internationalFields = this.props.internationalFields.map((field) => {
      // TODO: Should the key prefix be international?
      const key = `domestic-${field}`;
      switch (field) {
        case 'apoFpoDpoPostcode':
          return this.renderApoFpoDpoPostcode(key);
        case 'street':
          return (
            <Street
              attribute={`${this.props.attribute}.street`}
              name="street"
              label={this.props.internationalStreetLabel}
              className="mailing street"
              key={key}
              placeholder={this.props.streetPlaceholder}
              value={this.props.street}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
          );
        case 'city':
          return (
            <City
              attribute={`${this.props.attribute}.city`}
              name="city"
              key={key}
              label={this.props.cityLabel}
              placeholder={this.props.cityPlaceholder}
              value={this.props.city}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
          );
        case 'country':
          return (
            <Country
              isPartOfLocationModel
              attribute={`${this.props.attribute}.country`}
              name="country"
              key={key}
              label={this.props.countryLabel}
              {...countryValueResolver(this.props)}
              className="country"
              placeholder={this.props.countryPlaceholder}
              excludeUnitedStates="true"
              disabled={this.props.disabledCountry}
              onUpdate={this.updateCountry}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
              updateCountryFocused={this.props.updateCountryFocused}
            />
          );
        case 'militaryDiplomaticInstallationName':
          return this.renderMilitaryDiplomaticInstallationName(key);
        case 'usMilitaryInstallationDiplomaticFacility':
          // PVQ: What is the APO/FPO/DPO ZIP Code for the U.S. military installation or U.S. diplomatic facility?
          const apoFpoDpoPostcodeLabel: string = i18n.t('military.pvq.disciplinary.label.apofpodpozipcode');

          // PVQ: Is this a U.S. military installation or U.S. diplomatic facility?
          const branchLabel: string = i18n.t('address.pvq.installation.foreignBranch.label');

          // PVQ: What is the name of this U.S. military installation or U.S. diplomatic facility?
          const nameTitle: string = i18n.t('address.pvq.installation.foreign.title');

          const showApoFpoDpoPostcode = true;

          return this.renderMilitaryInstallation(
            apoFpoDpoPostcodeLabel,
            branchLabel,
            key,
            nameTitle,
            showApoFpoDpoPostcode,
          );
        default:
          return null;
      }
    });

    return (
      <div className="toggleable-location">
        <Branch
          attribute={setAttribute(this.props.attribute, 'layout')}
          label={this.props.label}
          name={this.props.name}
          onError={this.onError}
          onUpdate={this.updateToggle}
          required={this.props.required}
          value={branchValue(inUSA)}
          disabled={this.props.disabled}
        />
        {inUSA === true && this.props.domesticTitle && this.renderTitle(this.props.domesticTitle)}
        {inUSA === false && this.props.foreignTitle && this.renderTitle(this.props.foreignTitle)}
        {inUSA === true && domesticFields}
        {inUSA === false && internationalFields}
      </div>
    );
  }

  private renderApoFpoDpoPostcode(key: string): ReactElement {
    return (
      <ZipCode
        attribute={setAttribute(this.props.attribute, 'apoFpoDpoPostcode')}
        className="apo-fpo-dpo-postcode"
        key={key}
        label={this.props.zipcodeLabel}
        name="apoFpoDpoPostcode"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        onError={this.props.onError}
        onUpdate={this.updateApoFpoDpoPostcode}
        required={this.props.required}
        value={this.props.apoFpoDpoPostcode}
      />
    );
  }

  private renderMilitaryDiplomaticInstallationName(key: string): ReactElement {
    return (
      <Text
        attribute={setAttribute(this.props.attribute, 'militaryDiplomaticInstallationName')}
        key={key}
        label={this.props.militaryDiplomaticInstallationNameLabel}
        labelSize="h4"
        name="MilitaryDiplomaticInstallationName"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        onError={this.props.onError}
        onUpdate={this.updateMilitaryDiplomaticInstallationName}
        required={this.props.required}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        scrollIntoView={this.props.scrollIntoView}
        value={this.props.militaryDiplomaticInstallationName}
      />
    );
  }

  private renderMilitaryInstallation(
    apoFpoDpoPostcodeLabel: string,
    branchLabel: string,
    key: string,
    nameTitle: string,
    showApoFpoDpoPostcode: boolean,
  ): ReactElement {
    return (
      <MilitaryInstallation
        apoFpoDpoPostcode={this.props.apoFpoDpoPostcode}
        apoFpoDpoPostcodeLabel={apoFpoDpoPostcodeLabel}
        attribute={this.props.attribute}
        branchLabel={branchLabel}
        key={key}
        isForeignInstallation={false}
        isMilitaryDiplomaticAddress={this.props.isMilitaryDiplomaticAddress}
        militaryDiplomaticInstallationName={this.props.militaryDiplomaticInstallationName}
        militaryInstallationNameNotApplicable={this.props.militaryInstallationNameNotApplicable}
        militaryDiplomaticInstallationNameExplanation={this.props.militaryDiplomaticInstallationNameExplanation}
        nameTitle={nameTitle}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        onError={this.props.onError}
        onFocus={this.props.onFocus}
        onUpdate={this.updateMilitaryInstallation}
        showApoFpoDpoPostcode={showApoFpoDpoPostcode}
      />
    );
  }

  private renderTitle(title: string): ReactElement {
    return (<h4 className="fields-title">{title}</h4>);
  }
}

ToggleableLocation.defaultProps = {
  country: { value: 'United States' },
  domesticFields: [],
  domesticTitle: '',
  foreignTitle: '',
  internationalFields: [],
  militaryDiplomaticInstallationNameLabel: i18n.t('address.pvq.installation.name.title'),
  onError: (value, arr) => arr,
  required: false,
  scrollIntoView: false,
  updateCountryFocused: (focus) => { },
};

ToggleableLocation.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (!props.required) {
        return true;
      }

      // Organizing the validation tests in a structure
      const branchValidations = {
        Yes: {
          fields: (p) => p.domesticFields,
          city: (p) => (
            [Layouts.IDENTIFICATION_BIRTH_PLACE, Layouts.OFFENSE, Layouts.BIRTHPLACE].includes(p.layout)
              ? !!p.city || !!p.county
              : !!p.city),
          county: (p) => (
            [Layouts.IDENTIFICATION_BIRTH_PLACE, Layouts.OFFENSE, Layouts.BIRTHPLACE].includes(p.layout)
              ? !!p.city || !!p.county
              : !!p.county),
          state: (p) => !!p.state,
          stateZipcode: (p) => !!p.state && !!p.zipcode,
          country: (p) => !!countryString(p.country),
        },
        No: {
          fields: (p) => p.internationalFields,
          city: (p) => !!p.city,
          country: (p) => !!countryString(p.country),
        },
      };

      // Retrieve the branch value provided. If the value does match the
      // approved validation tree then return a negative response.
      const branch = branchValue(props.Location?.inUSA);
      const validations = branchValidations[branch];
      if (!validations) {
        return false;
      }

      // Loop through all of the fields based on the branch and test for values.
      for (let i = 0; i < validations.fields(props).length; i += 1) {
        const f = validations.fields(props)[i];
        // Retrieve the test and if one is not found print a warning and continue
        const test = validations[f];
        if (!test) {
          mappingWarning(f);
          return false;
        }

        if (!test(props)) {
          return false;
        }
      }

      return true;
    },
  },
];
